import type {
  ContextHubItem as ServerContextHubItem,
  ContextHubItemSourceType,
  MemoryCleanupSuggestionType,
} from '@server/types';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Re-declares the server's item, except `accountKey` and `connected` (ACT2-1133), which are picked from it.
 * Moving the rest onto the server type is a separate change.
 */
export interface ContextHubItem extends Pick<ServerContextHubItem, 'accountKey' | 'connected'> {
  id: string;
  kind: 'generated' | 'note' | 'memory' | 'skill';
  title: string;
  summary: string;
  content: string | null;
  searchText: string | null;
  status: 'generating' | 'ready' | 'failed';
  sourceType: ContextHubItemSourceType;
  sourceRef: string | null;
  sourceLabel: string | null;
  editable: boolean;
  createdViaAi: boolean;
  cleanupProvenance: {
    suggestionType: MemoryCleanupSuggestionType;
    sourceCount: number;
    resolvedAt: string;
    reviewerName: string | null;
  } | null;
  category: string | null;
  docType: 'source' | 'warehouse' | null;
  typeLabel: string | null;
  repoFullName: string | null;
  skillDescription?: string | null;
  skillEnabled?: boolean;
  createdBy?: string | null;
  reviewStatus: 'proposed' | 'approved' | 'superseded' | null;
  analysisStatus: 'pending' | 'running' | 'completed' | 'failed' | null;
  analysisError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContextReviewSuggestion {
  id: string;
  origin: 'capture' | 'scan' | 'chat';
  suggestionType: 'new' | 'duplicate' | 'merge' | 'conflict';
  proposalEntryId: string | null;
  sourceMemoryIds: string[];
  relatedContextRefs: { kind: ContextHubItem['kind']; id: string; title: string }[];
  proposedTitle: string | null;
  proposedContent: string | null;
  rationale: string;
  status: 'pending' | 'applied';
  createdAt: string;
}

export const CONTEXT_KIND_LABELS: Record<ContextHubItem['kind'], string> = {
  generated: 'Generated',
  note: 'Note',
  memory: 'Memory',
  skill: 'Skill',
};

export const CONTEXT_SOURCE_LABELS: Record<ContextHubItem['sourceType'], string> = {
  warehouse: 'Warehouse',
  manual: 'Manual note',
  chat: 'Chat',
  cleanup: 'Cleanup',
  mcp: 'Added via MCP',
};

/**
 * A note Lookout itself wrote (no author, AI-written): today, a background Warehouse orientation run
 * (ACT2-1320). The server labels the same notes `sourceLabel: 'Written by Lookout'`.
 */
export function isLookoutAuthored(item: Pick<ContextHubItem, 'kind' | 'createdBy' | 'createdViaAi'>): boolean {
  return item.kind === 'note' && item.createdViaAi && item.createdBy === null;
}

/**
 * The source badge on a Context card and detail page. A Lookout-authored note is stored with source
 * `manual`, so the plain lookup would call it a "Manual note" beside "Written by Lookout".
 */
export function contextSourceBadgeLabel(
  item: Pick<ContextHubItem, 'kind' | 'createdBy' | 'createdViaAi' | 'sourceType'>,
): string {
  return isLookoutAuthored(item) ? 'Lookout note' : CONTEXT_SOURCE_LABELS[item.sourceType];
}

/** The Context list without one deleted item, so the page does not show it while the list reloads. */
export function withoutContextItem<T extends Pick<ContextHubItem, 'id' | 'kind'>>(
  items: T[],
  kind: ContextHubItem['kind'],
  id: string,
): T[] {
  return items.filter((item) => item.id !== id || item.kind !== kind);
}

/** Reloads the Context list even when no page shows it, so the next visit does not paint stale rows. */
export function refreshContextList(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
  return queryClient.invalidateQueries({ queryKey, refetchType: 'all' });
}

export function getContextItemPath(item: Pick<ContextHubItem, 'id' | 'kind'>): string {
  if (item.kind === 'generated') return `/context/generated/${item.id}`;
  if (item.kind === 'skill') return `/context/skill/${item.id}`;
  return `/context/${item.kind}/${item.id}`;
}
